'use client';

import { c as _c } from "react/compiler-runtime";
import { SvgDonationFill, SvgHomeHeartFill, SvgMyFill, SvgStore } from '@common/icon';
import { cn } from '@core/utils';
import { useLinkStatus } from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { CustomLink } from '@/app/_components/CustomLink';
import { WelloPointTabItem } from '@/app/_components/WelloPointTabItem';
import { DONATION_ROUTES, MODULE_ROUTES, ROUTES, SELECTOR, SUB_DOMAIN, WEL_MARKET_ROUTES } from '@/constants';
import { useAdpopcornStatus } from '@/modules/adpopcorn';
import { MESSAGE_KEYWORD, useHandleApp } from '@/modules/wello-app';
import { useDeviceInfoStore } from '@/stores/DeviceStore';
import { gtmDatasets } from '@/utils/analytics/gtm';
type NavLinkItem = {
  kind: 'link';
  name: string;
  href: string;
  icon: typeof SvgHomeHeartFill;
  hasNotificationDot: boolean;
};
type NavAdpopcornItem = {
  kind: 'adpopcorn';
  name: '혜택';
};
type NavItem = NavLinkItem | NavAdpopcornItem;
const WELLO_NAVIGATION_ITEMS: readonly NavItem[] = [{
  kind: 'link',
  name: '홈',
  href: ROUTES.HOME.pathname,
  icon: SvgHomeHeartFill,
  hasNotificationDot: false
}, {
  kind: 'link',
  name: '고향사랑기부',
  href: DONATION_ROUTES.HOME.pathname,
  icon: SvgDonationFill,
  hasNotificationDot: false
}, {
  kind: 'link',
  name: '웰마켙',
  href: WEL_MARKET_ROUTES.HOME.pathname,
  icon: SvgStore,
  hasNotificationDot: false
}, {
  kind: 'adpopcorn',
  name: '혜택'
}, {
  kind: 'link',
  name: '마이페이지',
  href: ROUTES.MY.pathname,
  icon: SvgMyFill,
  hasNotificationDot: false
}];
const NAVIGATION_ANIMATION_DURATION = 300;
type GlobalNavigationLinkContentProps = Pick<NavLinkItem, 'name' | 'icon' | 'hasNotificationDot'> & {
  isActive: boolean;
  isAnimated: boolean;
};
const GlobalNavigationLinkContent = t0 => {
  const $ = _c(28);
  const {
    name,
    icon: Icon,
    hasNotificationDot,
    isActive,
    isAnimated
  } = t0;
  const {
    pending
  } = useLinkStatus();
  const iconContainerRef = useRef(null);
  let t1;
  let t2;
  if ($[0] !== pending) {
    t1 = () => {
      if (!pending) {
        return;
      }
      const linkElement = iconContainerRef.current?.closest("a");
      if (!linkElement) {
        return;
      }
      const preventRepeatedNavigation = _temp;
      linkElement.setAttribute("aria-busy", "true");
      linkElement.setAttribute("aria-disabled", "true");
      linkElement.addEventListener("click", preventRepeatedNavigation, true);
      return () => {
        linkElement.removeAttribute("aria-busy");
        linkElement.removeAttribute("aria-disabled");
        linkElement.removeEventListener("click", preventRepeatedNavigation, true);
      };
    };
    t2 = [pending];
    $[0] = pending;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== pending) {
    t3 = cn("relative transition-opacity", {
      "animate-pulse opacity-60 motion-reduce:animate-none": pending
    });
    $[3] = pending;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = isActive || pending;
  let t5;
  if ($[5] !== isAnimated || $[6] !== t4) {
    t5 = cn("size-28 text-Gray-400", {
      "animate-jump animate-duration-300": isAnimated,
      "text-Gray-black": t4
    });
    $[5] = isAnimated;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== Icon || $[9] !== t5) {
    t6 = <Icon className={t5} />;
    $[8] = Icon;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== hasNotificationDot) {
    t7 = hasNotificationDot ? <><span className="sr-only">새 소식 있음</span><div aria-hidden="true" className="absolute -right-4 -top-2 size-6 rounded-pill bg-R-500" /></> : null;
    $[11] = hasNotificationDot;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t3 || $[14] !== t6 || $[15] !== t7) {
    t8 = <div ref={iconContainerRef} className={t3}>{t6}{t7}</div>;
    $[13] = t3;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== pending) {
    t9 = cn("transition-colors", {
      "font-700 text-Gray-900": pending
    });
    $[17] = pending;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== name || $[20] !== t9) {
    t10 = <span className={t9}>{name}</span>;
    $[19] = name;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const t11 = pending ? "\uD398\uC774\uC9C0 \uC774\uB3D9 \uC911" : null;
  let t12;
  if ($[22] !== t11) {
    t12 = <span aria-live="polite" className="sr-only" role="status">{t11}</span>;
    $[22] = t11;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== t10 || $[25] !== t12 || $[26] !== t8) {
    t13 = <>{t8}{t10}{t12}</>;
    $[24] = t10;
    $[25] = t12;
    $[26] = t8;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  return t13;
};
export const GlobalNavigation = () => {
  const $ = _c(11);
  const pathname = usePathname();
  const [animatedHref, setAnimatedHref] = useState(null);
  const animationTimeoutRef = useRef(null);
  const isToss = useDeviceInfoStore(_temp2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      type: MESSAGE_KEYWORD.HAPTIC
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    request: requestHaptic
  } = useHandleApp(t0);
  const animateNavigationItem = href => {
    if (animationTimeoutRef.current) {
      window.clearTimeout(animationTimeoutRef.current);
    }
    setAnimatedHref(href);
    animationTimeoutRef.current = window.setTimeout(() => {
      setAnimatedHref(currentHref => currentHref === href ? null : currentHref);
      animationTimeoutRef.current = null;
    }, NAVIGATION_ANIMATION_DURATION);
  };
  let t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => () => {
      if (animationTimeoutRef.current) {
        window.clearTimeout(animationTimeoutRef.current);
      }
    };
    t2 = [];
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  const {
    isExternalWebview
  } = useAdpopcornStatus();
  let t3;
  if ($[3] !== isExternalWebview || $[4] !== isToss) {
    t3 = item => {
      if (isToss && item.kind === "link" && item.href === WEL_MARKET_ROUTES.HOME.pathname) {
        return false;
      }
      if (item.kind === "adpopcorn" && isExternalWebview) {
        return false;
      }
      return true;
    };
    $[3] = isExternalWebview;
    $[4] = isToss;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const navigationItems = WELLO_NAVIGATION_ITEMS.filter(t3);
  const t4 = "\uBA54\uC778 \uB0B4\uBE44\uAC8C\uC774\uC158";
  const t5 = "flex w-full justify-center";
  const t6 = SELECTOR;
  const t7 = "flex h-70 w-full items-center justify-around px-22";
  const t8 = navigationItems.map(item_0 => {
    if (item_0.kind === "adpopcorn") {
      return <li key={item_0.name} className="flex-1"><WelloPointTabItem /></li>;
    }
    const {
      name,
      href: href_0,
      icon,
      hasNotificationDot
    } = item_0;
    const clickFnbDataset = gtmDatasets.clickFnb({
      menu_name: name
    });
    const isActive = href_0 === ROUTES.HOME.pathname ? pathname === ROUTES.HOME.pathname || pathname === MODULE_ROUTES.KB_HOME.pathname : pathname === href_0 || pathname.startsWith(`${href_0}/`);
    const isCurrentPage = pathname === href_0;
    return <li key={name} className="flex-1"><CustomLink aria-current={isCurrentPage ? "page" : undefined} {...clickFnbDataset} className={cn("relative flex-col gap-5 whitespace-nowrap rounded-xxsmall font-500 text-Gray-550 transition-colors flex-center typo-caption1", {
        "text-Gray-900 font-700 cursor-default": isActive
      })} href={href_0} prefetch={true} onClick={event => {
        if (isCurrentPage) {
          event.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        requestHaptic();
        animateNavigationItem(href_0);
      }}><GlobalNavigationLinkContent hasNotificationDot={hasNotificationDot} icon={icon} isActive={isActive} isAnimated={animatedHref === href_0} name={name} /></CustomLink></li>;
  });
  let t9;
  if ($[6] !== t8) {
    t9 = <ul className={t7}>{t8}</ul>;
    $[6] = t8;
    $[7] = t9;
  } else {
    t9 = $[7];
  }
  let t10;
  if ($[8] !== t6.GLOBAL_NAVIGATION || $[9] !== t9) {
    t10 = <nav aria-label={t4} className={t5} id={t6.GLOBAL_NAVIGATION}>{t9}</nav>;
    $[8] = t6.GLOBAL_NAVIGATION;
    $[9] = t9;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  return t10;
};
function _temp(event) {
  event.preventDefault();
  event.stopPropagation();
}
function _temp2(state) {
  return state.subDomain === SUB_DOMAIN.TOSS;
}